import { useEffect, useRef, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { NavigationBar } from '../Components/navigationbar/NavigationBar';
import { Footer } from '../Components/Layout/footer/Footer';
import Layout from '../Components/Layout';
import styles from './landingPage.module.scss';
import MainButton from '../Components/common/buttons/MainButton';
import { buttonVariants } from '../Components/common/buttons/types';
import { contentSectionOne, contentSectionTwo, faqContent } from './landingPages/content';
import SectionCard from '../Components/common/Cards/SectionCard/SectionCard';
import { Faq } from '../Components/Faq/Faq';
import { FaqTypes, responsiveSizeJobs } from '../utils/constants';
import GlobalSearchandLocation from '../Components/molecules/GlobalSearchandLocation/GlobalSearchandLocation';
import { useDebounce, useWindowSize } from '../utils/hooks';
import { fetchSmartLocations } from '../actions/locations';
import { getSearchSuggestion } from '../actions/jobs';
import { loginRequest } from '../authConfig';
import colors from '../scss/colors';
import { logAppError, logAuthStep } from '../utils/logActions';

function useRevealOnScroll(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px', ...options }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
}

function Reveal({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) {
  const [ref, isVisible] = useRevealOnScroll();
  return (
    <Tag
      ref={ref}
      className={`${styles.reveal} ${isVisible ? styles.revealVisible : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function LandingPage() {
  // In Case if convert.com variant need to be used in the future
  // const { convertContext, isReady } = useConvert();
  const { width } = useWindowSize();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [faqData, setFaqData] = useState(faqContent(t).accounts);
  const [placeholderMobile, setPlaceholderMobile] = useState(false);
  const locationRef = useRef(null);
  const searchRef = useRef(null);
  const [searchFocused, setSearchFocused] = useState(width > responsiveSizeJobs);
  const [clickedLogin, setClickedLogin] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const sectionRef = useRef(null);
  const placeholderRef = useRef(null);
  const heroRef = useRef(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [filters, setFilters] = useState({
    searchTxt: '',
    city: '',
    country: '',
    state: '',
    location: '',
    suggestionTriggered: false
  });

  useEffect(() => {
    setFaqData(faqContent(t).accounts);
  }, [t]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setHeroLoaded(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleOnSelectFaq = (type) => {
    switch (type) {
      case FaqTypes.ACCOUNTS:
        setFaqData(faqContent(t).accounts);
        break;
      case FaqTypes.JOBS:
        setFaqData(faqContent(t).jobs);
        break;
      case FaqTypes.OTHERS:
        setFaqData(faqContent(t).others);
        break;
      default:
        setFaqData(faqContent(t).accounts);
    }
  };

  const fetchLocation = useDebounce((e) => {
    dispatch(fetchSmartLocations(e.target.value));
  }, 500);

  const fetchSearchSuggestions = useDebounce((e) => {
    dispatch(getSearchSuggestion(e.target.value));
  }, 500);

  const searchHandler = () => {
    setPlaceholderMobile(false);
    navigate({
      pathname: '/jobs',
      search:
        `&searchTxt=${filters.searchTxt}` +
        `&city=${filters.city}` +
        `&country=${filters.country}` +
        `&state=${filters.state}` +
        `&location=${filters.location}`
    });
  };

  const searchInputHandler = (newValue) => {
    setFilters({ ...filters, searchTxt: newValue });
    if (newValue.length > 0) locationRef.current.focus();
  };

  const locationHandler = (newValue) => {
    if (newValue?.length) {
      const [city, state, country] = newValue.trim().split(',');
      setFilters({ ...filters, city, state, country, location: '' });
      if (width < responsiveSizeJobs) setSearchFocused(false);
    }
  };

  const onInputHandler = (e) => {
    setFilters({ ...filters, city: '', state: '', country: '', location: e.target.value });
    if (e.target.value.trim().length > 2) {
      fetchLocation(e);
    }
  };

  const onKeyDownSearch = (e) => {
    setFilters({ ...filters, searchTxt: e.target.value });
    if (e.target.value.trim().length > 2) {
      fetchSearchSuggestions(e);
    }
  };

  const clearSearchHandler = () => {
    setFilters({ ...filters, searchTxt: '' });
  };

  const clearLocationHandler = () => {
    setFilters({ ...filters, city: '', state: '', country: '', location: '' });
  };

  const suggestionHandler = (newValue) => {
    const suggestion = newValue.trim().split('Jobs in');
    const isLocationSplit = suggestion[1].includes(',');
    const [city, state, country] = suggestion[1].trim().split(',');
    setFilters({
      ...filters,
      searchTxt: suggestion[0].trim(),
      location: !isLocationSplit ? suggestion[1].trim() : '',
      city: isLocationSplit ? city?.trim() : '',
      state: isLocationSplit ? state?.trim() : '',
      country: isLocationSplit ? country?.trim() : '',
      suggestionTriggered: true
    });
  };

  const locationValue = () => {
    if (filters?.city && filters.state && filters.country) {
      return `${filters?.city}, ${filters?.state}, ${filters?.country}`;
    }

    return '';
  };

  const onFocusHandler = () => {
    setSearchFocused(true);
  };

  useEffect(() => {
    const node = placeholderRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(entry.boundingClientRect.top < 0);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const Login = () => {
    setClickedLogin(true);
    return instance
      .loginRedirect({ ...loginRequest, redirectUri: `${window.location.origin}/dashboard` })
      .then(() => {
        logAuthStep('Login_Initiated_After_Click');
        setClickedLogin(false);
      })
      .catch((e) => {
        logAppError(e, 'Login_Init');
        console.error(e);
      });
  };

  if (isAuthenticated) {
    return (
      <Layout>
        {inProgress === 'none' && !window.location.pathname.includes('/jobs') ? (
          <Navigate to="/dashboard" />
        ) : (
          <Outlet />
        )}
      </Layout>
    );
  }

  return (
    <div className={`${styles.landingPage} flex flex-col sm:gap-0 gap-4`}>
      <div className="flex flex-row justify-center">
        <div className="flex flex-col p-0">
          <div className={`${styles.backgroundWrapper} ${styles.animatedBackground}`} ref={heroRef}>
            <div className="flex justify-center w-full">
              <NavigationBar isDark />
            </div>

            <div
              className={`flex justify-center text-center ${styles.heroItem} ${heroLoaded ? styles.heroItemIn : ''}`}
              style={{ transitionDelay: '60ms' }}
            >
              <h1>{t('landingPages.hero.title')}</h1>
            </div>
            <div
              className={`flex justify-center text-center ${styles.heroItem} ${heroLoaded ? styles.heroItemIn : ''}`}
              style={{ transitionDelay: '140ms' }}
            >
              <h2>{t('landingPages.hero.subtitle')}</h2>
            </div>
            <div
              className={`flex justify-center text-center ${styles.heroItem} ${heroLoaded ? styles.heroItemIn : ''}`}
              style={{ transitionDelay: '220ms' }}
            >
              <p className={styles.description}>{t('landingPages.hero.description')}</p>
            </div>
            <div
              className={`flex justify-center text-center ${styles.heroItem} ${heroLoaded ? styles.heroItemIn : ''}`}
              style={{ transitionDelay: '300ms' }}
            >
              <MainButton
                variant={buttonVariants.LIGHT_BLUE}
                rounded
                size="xmedium"
                action={() => Login()}
                customClass={`${styles.loginButton} ${styles.ctaHover}`}
                sx={{
                  fontWeight: '700',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)'
                }}
              >
                {clickedLogin ? (
                  <CircularProgress size="1rem" sx={{ color: colors.WHITE }} />
                ) : (
                  t('landingPages.hero.cta')
                )}
              </MainButton>
            </div>

            <div
              className={`${placeholderMobile ? styles.searchWrapperFocused : styles.searchWrapper} ${
                styles.heroItem
              } ${heroLoaded ? styles.heroItemIn : ''}`}
              style={{ transitionDelay: '380ms' }}
            >
              <div className={`${styles.searchBar} ${styles.searchBarAnimated}`}>
                <GlobalSearchandLocation
                  searchHandler={searchHandler}
                  setSearchValue={searchInputHandler}
                  searchValue={filters.searchTxt}
                  locationValue={filters.location || locationValue()}
                  setLocationValue={locationHandler}
                  onKeyDownHandler={onInputHandler}
                  onKeyDownSearch={onKeyDownSearch}
                  clearLocationHandler={clearLocationHandler}
                  clearSearchHandler={clearSearchHandler}
                  suggestionHandler={suggestionHandler}
                  onFocusHandler={onFocusHandler}
                  isSearchFocused={searchFocused}
                  placeholderMobileHandler={setPlaceholderMobile}
                  placeholderMobile={placeholderMobile}
                  locationRef={locationRef}
                  searchRef={searchRef}
                  searchButtonVariant={buttonVariants.PRIMARY}
                  suggestions
                />
                {width < responsiveSizeJobs && placeholderMobile && (
                  <div className={styles.containerButtons}>
                    <MainButton
                      size="xsmall"
                      variant={buttonVariants.LIGHT_BLUE}
                      customRadius="10px 10px 10px 10px"
                      action={searchHandler}
                      forceWidth="100%"
                      sx={{ marginTop: '10px' }}
                    >
                      {t('dashboard.jobs.search')}
                    </MainButton>
                  </div>
                )}
              </div>
            </div>
            {placeholderMobile && (
              <div
                role="presentation"
                className={`${styles.backdrop} ${styles.backdropFade}`}
                onClick={() => setPlaceholderMobile(false)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:gap-0 gap-4">
        {contentSectionOne(t).map((section, i) => (
          <div className="flex w-full justify-center" key={section.id}>
            <Reveal className={styles.sectionCard} delay={i * 60}>
              <SectionCard
                key={section.id}
                imagePosition={section.imagePosition}
                imageBackdrop={section.imageBackdrop}
                image={section.image}
                title={section.title}
                content={section.content}
                cardColor={section.cardColor}
                isDark={section.isDark}
              />
            </Reveal>
          </div>
        ))}
      </div>

      <div className="flex flex-row pt-6 w-full">
        <div className="flex flex-col w-full">
          <div ref={placeholderRef} />
          <div ref={sectionRef} className={`${isSticky ? styles.stickyCTA : styles.flyingCTA} ${styles.ctaTransition}`}>
            <span>{t('landingPages.hero.ctaTitle')}</span>
            <MainButton
              variant={buttonVariants.LIGHT_BLUE}
              rounded
              size="small"
              action={() => Login()}
              customClass={styles.ctaHover}
              sx={{
                fontWeight: 700,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)'
              }}
            >
              {clickedLogin ? (
                <CircularProgress size="1rem" sx={{ color: colors.WHITE }} />
              ) : (
                t('landingPages.hero.cta')
              )}
            </MainButton>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        {contentSectionTwo(t).map((section, i) => (
          <div className="flex justify-center" key={section.id}>
            <Reveal className={styles.sectionCard} delay={i * 60}>
              <SectionCard
                key={section.id}
                imagePosition={section.imagePosition}
                imageBackdrop={section.imageBackdrop}
                image={section.image}
                title={section.title}
                content={section.content}
                cardColor={section.cardColor}
                isDark={section.isDark}
              />
            </Reveal>
          </div>
        ))}
      </div>

      <div className="flex flex-col py-5">
        <div className="flex justify-center">
          <Reveal className={styles.faqWrapper}>
            <Faq onSelect={(type) => handleOnSelectFaq(type)} selectedData={faqData} />
          </Reveal>
        </div>
      </div>

      <div className="mt-auto p-0">
        <Footer />
      </div>
    </div>
  );
}
